import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { Inertia } from '@inertiajs/inertia';
import { InertiaLink, usePage } from '@inertiajs/inertia-react';
import Layout from '../../../Shared/Layout';
import DataContainer from '../../../Shared/DataContainer';
import TextInput from '../../../Shared/TextInput';
import DropdownButton from '../../../Shared/DropdownButton';
import LoadingButton from '../../../Shared/LoadingButton';
import Pagination from '../../../Shared/Pagination';
import Icon from '../../../Shared/Icon';
import { can, toFormData } from '../../../utils';

const Index = () => {
     const { auth, errors, data } = usePage().props;
     const [searching, setSearching] = useState(false);
     const [openSearch, setOpenSearch] = useState(false);
     const [values, setValues] = useState({
          name: data.filters.name || '',
     });

     function handleChange(e) {
          const key = e.target.name;
          const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
          setValues(values => ({
               ...values,
               [key]: value
          }));
     }

     function toggleSearch(e) {
          e.preventDefault();
          setOpenSearch(!openSearch);
     }

     function handleSearch(e) {
          e.preventDefault();
          setSearching(true);
          const value = {
               name: values.name,
          };
          Inertia.post(route('user.search'), value, { preserveState: false }).then(() => {
               setSending(false);
          });
     }

     return (
          <React.Fragment key="security-index">
               <Helmet>
                    <title>Users</title>
               </Helmet>
               <div className="max-w-7xl mx-auto p-2">
                    <InertiaLink className="font-semibold text-md text-gray-700 hover:text-gray-800 leading-tight" href={route('home')}>Dashboard</InertiaLink> | <span className="text-md text-gray-700 leading-tight">Users</span>
               </div>
               <DataContainer>
                    <div className="col-span-12">
                         {can(auth.user, 'create-user') && (<InertiaLink href={route('user.create')} className="bg-gray-300 hover:bg-gray-400 mr-2 text-gray-800 text-sm py-1 px-1 rounded inline-flex items-center">
                              <Icon name={'plus'} className={'fill-current w-4 h-4 mr-2'} />
                              Add User
                         </InertiaLink>)}
                         <InertiaLink onClick={e => toggleSearch(e)} href={route('')} className="bg-gray-300 hover:bg-gray-400 mr-2 text-gray-800 text-sm py-1 px-1 rounded inline-flex items-center">
                              <Icon name={'search'} className={'fill-current w-4 h-4 mr-2'} />
                              Search
                         </InertiaLink>
                         <InertiaLink href={route('user.index')} className={`text-gray-500 hover:underline text-sm inline-flex items-center`}>
                              Clear
                         </InertiaLink>
                    </div>
                    <div className={`col-span-12 border p-2 rounded bg-gray-100 ${openSearch ? 'block' : 'hidden'}`}>
                         <h2 className="text-md font-semibold text-gray-700">Search</h2>
                         <form className="w-full" onSubmit={handleSearch}>
                              <div className="grid grid-cols-3 gap-2">
                                   <TextInput
                                        className="form-input rounded-md shadow-sm block"
                                        label="Name, E-mail or Username"
                                        name="name"
                                        type="text"
                                        disable={false}
                                        readonly={false}
                                        must={false}
                                        errors={errors.name}
                                        value={values.name}
                                        onChange={handleChange}
                                   />
                                   <div className="inline-block relative">
                                        <LoadingButton type="submit" loading={searching} className="w-full mt-5 text-center items-center px-4 py-3 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 active:bg-gray-900 focus:outline-none focus:border-gray-900 focus:ring-gray disabled:opacity-25 transition ease-in-out duration-150">
                                             Search
                                        </LoadingButton>
                                   </div>
                              </div>
                         </form>
                    </div>
                    <table className="table-fixed col-span-12">
                         <thead className="bg-gray-400">
                              <tr>
                                   <th className="px-4 py-2">Name</th>
                                   <th className="px-4 py-2">Username</th>
                                   <th className="px-4 py-2">Email</th>
                                   <th className="px-4 py-2">Roles</th>
                                   <th className="px-4 py-2"></th>
                              </tr>
                         </thead>
                         <tbody>
                              {data.users.data.map(({id, name, username, email, avatar, roles}) => {
                                   return <tr key={id}>
                                        <td className="border px-4 py-2">{name}</td>
                                        <td className="border px-4 py-2">{username}</td>
                                        <td className="border px-4 py-2">{email}</td>
                                        <td className="border px-4 py-2">{roles.join(', ')}</td>
                                        <td className="border px-4 py-2">
                                             <DropdownButton caption="Actions" color="blue">
                                                  {can(auth.user, 'update-user') && (<InertiaLink href={route('user.edit', id)} className="flex block px-6 py-2 text-gray-600 hover:bg-gray-100 hover:text-gray-700">
                                                       <Icon name={'edit'} className={'fill-current w-5 h-5 mr-2'} />
                                                       Edit
                                                  </InertiaLink>)}
                                                  {can(auth.user, 'assign-role') && (<InertiaLink href={route('user.get.role', id)} className="flex block px-6 py-2 text-gray-600 hover:bg-gray-100 hover:text-gray-700">
                                                       <Icon name={'cheveron-right'} className={'fill-current w-5 h-5 mr-2'} />
                                                       Assign Role
                                                  </InertiaLink>)}
                                             </DropdownButton>
                                        </td>
                                   </tr>
                              })}
                              {!data.users.data.length && (<tr>
                                   <td colSpan="5" className="p-4 bg-blue-100 text-blue-500 text-center">No data found.</td>
                              </tr>)}
                         </tbody>
                    </table>
                    <Pagination links={data.users.links} />
               </DataContainer>
          </React.Fragment>
     );
};

// Persisten layout
// Docs: https://inertiajs.com/pages#persistent-layouts
Index.layout = page => <Layout children={page} header={'Users'} />;

export default Index;
